import { combineReducers } from 'redux';
import omit from 'lodash/omit';
import keyBy from 'lodash/keyBy';
import {
  FETCH_CONTESTS_SUCCESS,
  UPDATE_CONTEST_CLOSE,
  OMIT_CONTEST,
  UPDATE_CONTEST,
  FETCH_CONTESTS_STATUS,
  ADD_CONTEST_PLAYED,
  TOGGLE_SELECT_DURATION,
  TOGGLE_SELECT_COIN,
  TOGGLE_VIEW,
  SET_SELECTED_CONTEST,
  SET_SELECTED_COIN,
  SET_SELECTED_DURATION,
  UPDATE_STATUS,
} from './types';
import { getContestSeriesKey } from './actions';
import { allCoinLists } from '../../utils/getBodPrices';
import { loadSavedStatte } from '../../utils/utils';

const coinsOrder = Object.assign({}, ...allCoinLists.moonrekt.map((k, i) => ({ [k]: i })));
const coinSort = (a, b) => coinsOrder[a] - coinsOrder[b];

const defaultViewState = {
  currentView: 'carousel',
  selectedCoins: ['BTC'],
  selectedDurations: ['00:01:00'],
}

const ParimutuelsViewReducer = (state = loadSavedStatte('contestsState', defaultViewState), { type, payload }) => {
  switch (type) {
    case TOGGLE_SELECT_DURATION: {
      let { selectedDurations } = state;

      if (!selectedDurations.includes(payload)) {
        return { ...state, selectedDurations: [...selectedDurations, payload].sort() };
      }

      if (selectedDurations.length < 2) {
        return state;
      }

      return { ...state, selectedDurations: selectedDurations.filter((c) => c !== payload) };
    }

    case TOGGLE_SELECT_COIN: {
      const { selectedCoins } = state;

      if (!selectedCoins.includes(payload)) {
        return { ...state, selectedCoins: [...selectedCoins, payload].sort(coinSort) };
      }

      if (selectedCoins.length < 2) {
        return state;
      }

      return { ...state, selectedCoins: selectedCoins.filter((c) => c !== payload) };
    }

    case TOGGLE_VIEW: {
      const currentView = (state.currentView === 'pro') ? 'carousel' : 'pro';
      const {
        selectedCoins: [selectedCoin],
        selectedDurations: [selectedDuration],
      } = state;

      return {
        ...state,
        currentView,
        selectedCoins: [selectedCoin],
        selectedDurations: [selectedDuration],
      };
    }

    case SET_SELECTED_CONTEST:
      return { ...state, selectedContest: payload };

    case SET_SELECTED_COIN:
      return { ...state, selectedCoins: [payload] };

    case SET_SELECTED_DURATION:
      return { ...state, selectedDurations: [payload] };

    default:
      return state;
  }
};

const ParimutuelCoins = (state = [], { type, payload }) => {
  switch (type) {

    case FETCH_CONTESTS_SUCCESS:
      if (!payload || payload.length === 0) {
        return state;
      }

      return [...payload.reduce((stateDraft, contest) => {
        if (contest.assetType) {
          return [...new Set([...stateDraft, contest.assetType, "USDC_SPL"])]
        }
      }, state)]

    default:
      return state;
  }
};

const ContestPlayedReducer = (state = {}, { type, payload }) => {
  switch (type) {
    case ADD_CONTEST_PLAYED:
      const { id } = payload.contest;
      const playContest = { ...state[id], ...payload.contest };

      if (playContest.position === 'none') {
        return omit(state, id);
      }

      return { ...state, [id]: playContest };

    case OMIT_CONTEST:

      const { idToOmit } = payload;
      if (!state[idToOmit]) {
        return state;
      }

      return omit(state, idToOmit);

    default:
      return state;
  }
};

const SeriesReducer = (state: any = { isFetched: true }, { type, payload }) => {
  switch (type) {

    case FETCH_CONTESTS_SUCCESS:
      if (!payload || payload.length === 0) {
        return state;
      }

      return payload.reduce(
        (drafState:any, contest:any) => {
          const seriesKey = getContestSeriesKey(contest);
          const contests = drafState[seriesKey] || [];
          if (!contests.includes(contest.id)) {
            drafState[seriesKey] = [...contests, contest.id];
          }
          return drafState;
        },
        { ...state }
      );

    case OMIT_CONTEST:
      if (!payload || !state[payload.seriesKey]) {
        return state;
      }

      const { seriesKey, idToOmit } = payload;

      return {
        ...state,
        [seriesKey]: state[seriesKey].filter((id:any) => id !== idToOmit),
      }
    case FETCH_CONTESTS_STATUS: 
      return {
        ...state,
        isFetched: payload
      }

    default:
      return state;
  }
};

const ContestsReducer = (state:any = {}, { type, payload }:{type: any, payload:any}) => {
  switch (type) {
    case UPDATE_STATUS:
      const now = Math.floor(Date.now() / 1000);
      if (now % 60 > 0) {
        return state;
      }

      return Object.keys(state).reduce((acc, id) => {
        const contest = state[id];
        const contestCloseTime = Date.parse(contest.contestCloseTime) / 1000;
        const contestLiveTime = Date.parse(contest.contestLiveTime) / 1000;

        if (contestCloseTime === now && contest.status === 'Live') {
          acc[id] = { ...contest, status: 'Closing' };
        } else if (contestLiveTime === now && contest.status === 'Opened') {
          acc[id] = { ...contest, status: 'Live' };
        }

        return acc;
      }, { ...state });

    case FETCH_CONTESTS_SUCCESS:
      if (!payload || payload.length === 0) {
        return state;
      }

      const keyedContests = keyBy(payload, 'id');
      return { ...state, ...keyedContests };

    case UPDATE_CONTEST:
      const { id, ...contest } = payload;
      if (!state[id]) {
        return state;
      }

      return {
        ...state,
        [id]: { ...state[id], ...contest },
      };

    case UPDATE_CONTEST_CLOSE: {
      const { id, status, pricing: instrumentPrices } = payload.contestUpdate;

      if (!state[id]) {
        return state;
      }

      return {
        ...omit(state, payload.idToOmit),
        [id]: { ...state[id], status, instrumentPrices },
      };
    }

    default:
      return state;
  }
};

export default combineReducers({
  view: ParimutuelsViewReducer,
  series: SeriesReducer,
  contests: ContestsReducer,
  contestPlayed: ContestPlayedReducer,
  parimutuelCoins: ParimutuelCoins,
});
